import { useRef } from "react";

interface InlineCommit {
  /**
   * The value the editor starts from: the latest commit's value while that commit is unsettled,
   * else the last value whose write resolved, else `initial`. A rolled-back commit does not leave
   * an older in-flight value as the baseline. Read at call time, from an event handler.
   */
  baseline: () => string;
  /**
   * Writes `next` unless it is the baseline. Only the latest commit rolls back: when its write is
   * rejected the baseline falls back to the last RESOLVED value (or `initial`), so entering `next`
   * again retries it, and the restored baseline is handed to `onRollback`.
   */
  commit: (
    next: string,
    write: () => void | Promise<void>,
    onRollback?: (restored: string) => void,
  ) => Promise<void>;
}

/** `InlineCommit` with the row's current value passed on each call instead of bound to a render. */
interface InlineCommitState {
  baseline: (initial: string) => string;
  commit: (
    next: string,
    initial: string,
    write: () => void | Promise<void>,
    onRollback?: (restored: string) => void,
  ) => Promise<void>;
}

/** A value handed to `write`, with its place in the commit order. */
interface SentValue {
  value: string;
  order: number;
}

/**
 * The commit/rollback rules of an inline row editor, kept apart from React so they can be tested
 * without a renderer. Two values are tracked separately:
 * - pending: the latest value sent whose write has not settled. It is the baseline while it lasts,
 *   so blurring again with the same value sends nothing.
 * - confirmed: the last value whose write RESOLVED, by commit order — a slow older success that
 *   lands after a newer one never moves it back.
 * When writes overlap (B is sent before A settles), A is superseded: its rejection does nothing,
 * and its success only counts if no later commit was confirmed first. B's rejection restores the
 * confirmed value, never A while A is unsaved, so a value that was never saved is never promoted.
 * If A resolves after B rolled back, A becomes the confirmed value: it is what was saved last.
 */
export function createInlineCommit(): InlineCommitState {
  let sent = 0;
  let pending: SentValue | null = null;
  let confirmed: SentValue | null = null;

  const baseline = (initial: string) => pending?.value ?? confirmed?.value ?? initial;

  async function commit(
    next: string,
    initial: string,
    write: () => void | Promise<void>,
    onRollback?: (restored: string) => void,
  ) {
    if (next === baseline(initial)) return;
    sent += 1;
    const current: SentValue = { value: next, order: sent };
    pending = current;
    try {
      await write();
    } catch {
      // The caller already reported the failure. A superseded commit leaves the later one alone;
      // the latest falls back to the confirmed value so the same value can be retried.
      if (pending !== current) return;
      pending = null;
      onRollback?.(baseline(initial));
      return;
    }
    if (confirmed === null || confirmed.order < current.order) confirmed = current;
    if (pending === current) pending = null;
  }

  return { baseline, commit };
}

/**
 * The last saved value of an inline row editor: the row prop stays stale until the refetch lands,
 * so `initial` (the row's value) is only the baseline until a commit is sent (see
 * `createInlineCommit` for the overlapping-writes rules). Knows nothing about the value itself —
 * the editor validates before committing, and the caller of `write` reports a failure. The rules
 * live in one instance per editor, kept in a ref and created on first use; each call passes this
 * render's `initial`.
 */
export function useInlineCommit(initial: string): InlineCommit {
  const instance = useRef<InlineCommitState | null>(null);
  // Only called from the returned functions (event handlers), never during render.
  const rules = () => {
    if (instance.current === null) instance.current = createInlineCommit();
    return instance.current;
  };

  return {
    baseline: () => rules().baseline(initial),
    commit: (next, write, onRollback) => rules().commit(next, initial, write, onRollback),
  };
}
